import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { newIdempotencyKey } from '../api/client';
import type { KnowledgeCredentialField, KnowledgeKind, KnowledgeSource, Problem } from '../api/types';
import { Button } from '../components/Button';
import { ProblemNotice } from '../components/ProblemNotice';
import { usePlatform } from '../app/platform';
import { toProblem } from '../app/useAsync';
import type { FieldIssue } from '../forms/schema';
import {
  credentialValue,
  settingsDraft,
  settingsFields,
  settingsValue,
  type FormDraft,
  type SettingsField,
} from './knowledgeSourceForm';

/** api の SourceCreate / SourcePatch の上限（外れると欄の分からない 422 invalid_request になる）。 */
const NAME_MAX_CHARS = 100;
const DESCRIPTION_MAX_CHARS = 2000;

export interface KnowledgeSourceDialogProps {
  workspaceId: string;
  kind: KnowledgeKind;
  /** null なら新規の接続、あれば編集。 */
  source: KnowledgeSource | null;
  onSaved(source: KnowledgeSource): void;
  onClose(): void;
}

/**
 * 外部ナレッジソースの接続フォーム（S6 E16・E17）。欄は kinds の settingsSchema から描くので、
 * 種類が増えても画面を変えずに済む。設定値と資格情報は AI に見せないので、説明欄にその旨を書く。
 * 資格情報は state に持つだけで保存も再表示もしない（編集の空欄は「変えない」）。
 */
export function KnowledgeSourceDialog({ workspaceId, kind, source, onSaved, onClose }: KnowledgeSourceDialogProps) {
  const { api } = usePlatform();
  const dialogRef = useRef<HTMLDialogElement>(null);
  const titleId = useId();
  const idPrefix = useId();
  const editing = source !== null;
  const fields = useMemo(() => settingsFields(kind.settingsSchema), [kind]);
  const [name, setName] = useState(source?.name ?? kind.name);
  const [description, setDescription] = useState(source?.description ?? '');
  const [draft, setDraft] = useState<FormDraft>(() => settingsDraft(fields, source?.settings));
  const [secrets, setSecrets] = useState<Record<string, string>>({});
  const [issues, setIssues] = useState<FieldIssue[]>([]);
  const [problem, setProblem] = useState<Problem | null>(null);
  const [saving, setSaving] = useState(false);
  // 新規は開いている間ずっと同じ冪等キー（二度押しで接続が 2 つできない）。編集は押すたびに新しい鍵。
  const [createKey] = useState(() => newIdempotencyKey());

  // マウントしたら開く（DeleteDialog と同じ作法）。cleanup では閉じない。
  useEffect(() => {
    const dialog = dialogRef.current;
    if (!dialog || dialog.open) return;
    dialog.showModal();
  }, []);

  const issueOf = (key: string) => issues.find((issue) => issue.key === key)?.message ?? null;
  const nameIssue = issueOf('name');

  const submit = async () => {
    if (saving) return;
    // 押した時点で前回のサーバー応答は消す（欄の指摘と前回の 422 が同時に出ないようにする）。
    setProblem(null);
    const settings = settingsValue(fields, draft);
    const credential = credentialValue(kind.credentialFields, secrets, editing);
    // 名前は api の min_length=1。手元で見ないと欄の分からない 422 invalid_request になる。
    const nameIssues: FieldIssue[] = name.trim() === '' ? [{ key: 'name', message: '必須項目です。' }] : [];
    const found = [...nameIssues, ...settings.issues, ...credential.issues];
    setIssues(found);
    if (found.length > 0) return;
    setSaving(true);
    try {
      const saved =
        source === null
          ? await api.createKnowledgeSource(
              workspaceId,
              {
                kind: kind.kind,
                name: name.trim(),
                description,
                settings: settings.value,
                ...(credential.value ? { credential: credential.value } : {}),
              },
              createKey,
            )
          : await api.updateKnowledgeSource(
              workspaceId,
              source.id,
              {
                expectedRevision: source.revision,
                name: name.trim(),
                description,
                settings: settings.value,
                ...(credential.value ? { credential: credential.value } : {}),
              },
              newIdempotencyKey(),
            );
      onSaved(saved);
    } catch (error) {
      setProblem(toProblem(error));
    } finally {
      setSaving(false);
    }
  };

  const cancel = () => {
    const dialog = dialogRef.current;
    if (dialog?.open) dialog.close();
    else onClose();
  };

  return (
    <dialog
      ref={dialogRef}
      className="dialog knowledge-source-dialog"
      aria-labelledby={titleId}
      onCancel={(event) => {
        if (saving) event.preventDefault();
      }}
      onClose={onClose}
    >
      <div className="dialog-head">
        <h2 id={titleId} className="dialog-title">
          {source === null ? `${kind.name} を接続` : `${source.name} の接続を編集`}
        </h2>
      </div>
      <div className="dialog-body">
        <div className={nameIssue === null ? 'field' : 'field field-invalid'}>
          <label className="field-label" htmlFor={`${idPrefix}-name`}>
            名前
          </label>
          <input
            {...fieldControl(`${idPrefix}-name`, nameIssue, saving)}
            maxLength={NAME_MAX_CHARS}
            value={name}
            onChange={(event) => setName(event.target.value)}
          />
          {nameIssue !== null && (
            <p className="field-error" id={`${idPrefix}-name-error`} role="alert">
              {nameIssue}
            </p>
          )}
        </div>
        <div className="field">
          <label className="field-label" htmlFor={`${idPrefix}-description`}>
            説明
          </label>
          <textarea
            {...fieldControl(`${idPrefix}-description`, null, saving)}
            rows={2}
            maxLength={DESCRIPTION_MAX_CHARS}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
          />
          <p className="hint knowledge-source-field-hint">
            AI に見せる範囲の説明です（例: 厚生労働省のサイトだけを検索する）。設定値と資格情報は AI に見せません。
          </p>
        </div>
        {fields.map((field) => (
          <SettingsRow
            key={field.key}
            id={`${idPrefix}-${field.key}`}
            field={field}
            value={draft[field.key] ?? ''}
            issue={issueOf(field.key)}
            disabled={saving}
            onChange={(next) => setDraft((current) => ({ ...current, [field.key]: next }))}
          />
        ))}
        {kind.credentialFields.length > 0 && (
          <>
            <h3 className="dialog-title">資格情報</h3>
            {kind.credentialFields.map((field) => (
              <CredentialRow
                key={field.key}
                id={`${idPrefix}-cred-${field.key}`}
                field={field}
                value={secrets[field.key] ?? ''}
                issue={issueOf(field.key)}
                disabled={saving}
                configured={editing && source !== null && source.hasCredential}
                onChange={(next) => setSecrets((current) => ({ ...current, [field.key]: next }))}
              />
            ))}
          </>
        )}
        <ProblemNotice problem={problem} />
        {problem !== null && problem.code === 'idempotency_conflict' && (
          <p className="dialog-hint">
            前に送った内容と違うので、この画面からは送り直せません。いったん閉じて開き直してください（前の保存が済んでいれば一覧に出ます）。
          </p>
        )}
      </div>
      <div className="dialog-actions">
        <Button disabled={saving} onClick={cancel}>
          キャンセル
        </Button>
        <Button variant="primary" disabled={saving} onClick={() => void submit()}>
          {saving ? '保存中…' : '保存する'}
        </Button>
      </div>
    </dialog>
  );
}

/** 欄の入力に共通で渡す属性。StandardForm と同じく field-input・aria-invalid・aria-describedby をそろえる。 */
interface FieldControl {
  id: string;
  className: string;
  disabled: boolean;
  'aria-invalid': boolean;
  'aria-describedby': string | undefined;
}

function fieldControl(id: string, issue: string | null, disabled: boolean): FieldControl {
  return {
    id,
    className: 'field-input',
    disabled,
    'aria-invalid': issue !== null,
    'aria-describedby': issue === null ? undefined : `${id}-error`,
  };
}

interface SettingsRowProps {
  id: string;
  field: SettingsField;
  value: string | boolean;
  issue: string | null;
  disabled: boolean;
  onChange(next: string | boolean): void;
}

function SettingsRow({ id, field, value, issue, disabled, onChange }: SettingsRowProps) {
  const text = typeof value === 'string' ? value : '';
  const control = fieldControl(id, issue, disabled);
  return (
    <div className={issue === null ? 'field' : 'field field-invalid'}>
      <label className="field-label" htmlFor={id}>
        {field.label}
      </label>
      {field.kind === 'lines' && (
        <textarea {...control} rows={4} value={text} onChange={(event) => onChange(event.target.value)} />
      )}
      {field.kind === 'boolean' && (
        <input
          {...control}
          type="checkbox"
          checked={value === true}
          onChange={(event) => onChange(event.target.checked)}
        />
      )}
      {(field.kind === 'integer' || field.kind === 'number') && (
        <input {...control} type="number" value={text} onChange={(event) => onChange(event.target.value)} />
      )}
      {field.kind === 'text' && (
        <input {...control} type="text" value={text} onChange={(event) => onChange(event.target.value)} />
      )}
      {field.hint !== null && <p className="hint knowledge-source-field-hint">{field.hint}</p>}
      {issue !== null && (
        <p className="field-error" id={`${id}-error`} role="alert">
          {issue}
        </p>
      )}
    </div>
  );
}

interface CredentialRowProps {
  id: string;
  field: KnowledgeCredentialField;
  value: string;
  issue: string | null;
  disabled: boolean;
  /** 編集で既に設定済み。空のままなら今の値を残す。 */
  configured: boolean;
  onChange(next: string): void;
}

function CredentialRow({ id, field, value, issue, disabled, configured, onChange }: CredentialRowProps) {
  return (
    <div className={issue === null ? 'field' : 'field field-invalid'}>
      <label className="field-label" htmlFor={id}>
        {field.label}
      </label>
      <input
        {...fieldControl(id, issue, disabled)}
        type={field.secret ? 'password' : 'text'}
        autoComplete="off"
        spellCheck={false}
        value={value}
        {...(configured ? { placeholder: '設定済み（変えるときだけ入力）' } : {})}
        onChange={(event) => onChange(event.target.value)}
      />
      {issue !== null && (
        <p className="field-error" id={`${id}-error`} role="alert">
          {issue}
        </p>
      )}
    </div>
  );
}
